import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Match, Team, Tournament } from '@/types/cricket';
import { format } from 'date-fns';
import { Calendar, MapPin, Trophy, Users, Clock } from 'lucide-react';
import { getInningsDisplay } from '@/utils/cricket/displayUtils';

interface MatchHoverCardProps {
  match: Match;
  team1?: Team;
  team2?: Team;
  tournament?: Tournament;
  children: React.ReactNode;
}

const MatchHoverCard: React.FC<MatchHoverCardProps> = ({
  match,
  team1,
  team2,
  tournament,
  children
}) => {
  const getFormattedDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'PPP p');
    } catch (e) {
      return dateString;
    }
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-80 p-4"
        side="right" 
        align="center"
      >
        <div className="space-y-3">
          <h4 className="font-semibold mb-2">Match Info</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="text-gray-600">Match Type:</div>
            <div className="font-medium">{match.matchType || 'T20'}</div>

            <div className="text-gray-600">Total Overs:</div>
            <div className="font-medium">{match.totalOvers || 20}</div>

            <div className="text-gray-600">City:</div>
            <div className="font-medium">{match.city}</div>

            <div className="text-gray-600">Balls per Over:</div>
            <div className="font-medium">{match.ballsPerOver || 6}</div>

            <div className="text-gray-600">Wickets per Innings:</div>
            <div className="font-medium">{match.wicketsPerInnings || 10}</div>

            <div className="text-gray-600">Status:</div>
            <div className={`font-medium capitalize ${
              match.status === 'live' ? 'text-green-600' :
              match.status === 'completed' ? 'text-blue-600' :
              match.status === 'upcoming' ? 'text-yellow-500' :
              'text-gray-600'
            }`}>
              {match.status}
            </div>

            {/* UPCOMING: Show only basic info */}
            {match.status === 'upcoming' && (
              <>
                {match.matchDate && (
                  <>
                    <div className="text-gray-600">Start Date:</div>
                    <div className="font-medium">{getFormattedDate(match.matchDate)}</div>
                  </>
                )}
                {match.venue && (
                  <>
                    <div className="text-gray-600">Venue:</div>
                    <div className="font-medium">{match.venue}</div>
                  </>
                )}
                {match.tossResult && (
                  <>
                    <div className="text-gray-600">Toss:</div>
                    <div className="font-medium">{match.tossResult}</div>
                  </>
                )}
              </>
            )}

            {/* LIVE: Show dynamic info */}
            {match.status === 'live' && (
              <>
                <div className="text-gray-600">Current Innings:</div>
                <div className="font-medium">
                  {getInningsDisplay(match.currentInnings)} - {match.currentBattingTeam === team1?.id ? team1?.name : team2?.name}
                </div>
                <div className="text-gray-600">Current Score:</div>
                <div className="font-medium">
                  {match.currentBattingTeam === team1?.id
                    ? `${match.team1Score.runs}/${match.team1Score.wickets} (${match.team1Score.overs}.${match.team1Score.balls} ov)`
                    : `${match.team2Score.runs}/${match.team2Score.wickets} (${match.team2Score.overs}.${match.team2Score.balls} ov)`}
                </div>
                <div className="text-gray-600">Run Rate:</div>
                <div className="font-medium">
                  {match.currentBattingTeam === team1?.id
                    ? match.team1Score.overs + match.team1Score.balls / (match.ballsPerOver || 6) > 0
                      ? ((match.team1Score.runs / (match.team1Score.overs + match.team1Score.balls / (match.ballsPerOver || 6))).toFixed(2))
                      : '0.00'
                    : match.team2Score.overs + match.team2Score.balls / (match.ballsPerOver || 6) > 0
                      ? ((match.team2Score.runs / (match.team2Score.overs + match.team2Score.balls / (match.ballsPerOver || 6))).toFixed(2))
                      : '0.00'}
                </div>
                {/* Optionally, show required runs/balls if chasing */}
                {match.target && (
                  <>
                    <div className="text-gray-600">Target:</div>
                    <div className="font-medium">{match.target} runs</div>
                  </>
                )}
              </>
            )}

            {/* COMPLETED: Show result, man of the match, final scores */}
            {match.status === 'completed' && (
              <>
                {match.result && (
                  <>
                    <div className="text-gray-600">Result:</div>
                    <div className="font-medium text-blue-600">{match.result}</div>
                  </>
                )}
                {match.manOfTheMatch && (
                  <>
                    <div className="text-gray-600">Man of the Match:</div>
                    <div className="font-medium">{match.manOfTheMatch}</div>
                  </>
                )}
                <div className="text-gray-600">Final Scores:</div>
                <div className="font-medium">
                  {team1?.name}: {match.team1Score.runs}/{match.team1Score.wickets} ({match.team1Score.overs}.{match.team1Score.balls} ov)
                  <br />
                  {team2?.name}: {match.team2Score.runs}/{match.team2Score.wickets} ({match.team2Score.overs}.{match.team2Score.balls} ov)
                </div>
                <div className="text-gray-600">Last Innings:</div>
                <div className="font-medium">
                  {getInningsDisplay(match.currentInnings)} - {match.currentBattingTeam === team1?.id ? team1?.name : team2?.name}
                </div>
              </>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default MatchHoverCard;
